import { useEffect, useState } from 'react'
// Relative import on purpose: jest resolves no src-absolute specifiers, and
// this hook is exercised directly in specs (the 'helpers' barrel would break
// module resolution there)
import { api } from '../helpers'
import { useSpecificFeature } from './useSpecificFeature'

// Last known arc-mosaico-project flag value: the flag fetches only resolve
// after the first paint, so without this cache Arc users would see the legacy
// Malva/turquoise theme flash before the rebranding kicks in (try/catch:
// localStorage may be unavailable, same pattern as context/useLocalStorage)
export const ARC_BRANDING_STORAGE_KEY = 'arc-branding-enabled'

// id of the <style> tag that carries the @view-transition at-rule (see below)
export const ARC_VIEW_TRANSITION_STYLE_ID = 'arc-view-transition'

interface ISchoolScopedResult {
  enabled: boolean
  /** False when the endpoint gave no real answer (logged out, network error) */
  resolved: boolean
}

// Arc flags are school-scoped by design (Api::ArcMosaico::FeaturesController):
// the global Flipper flag may be enabled platform-wide (as on staging) while
// individual legacy schools never received it in schools.features. The
// school-scoped endpoint is therefore the branding AUTHORITY; the global
// Flipper answer is only a fallback for logged-out pages (signin/signup),
// where no school context exists. Module-level promise so concurrent mounts
// share one request (same pattern as useSpecificFeature).
let schoolScopedRequest: Promise<ISchoolScopedResult> | null = null

const fetchSchoolScopedArcBranding = (): Promise<ISchoolScopedResult> => {
  if (schoolScopedRequest) return schoolScopedRequest

  schoolScopedRequest = api
    .get('/api/arc_mosaico/features/current')
    .then(response => {
      const features = response.data?.features
      if (!Array.isArray(features)) return { enabled: false, resolved: false }
      return {
        enabled: features.some(
          (feature: { key: string; state: string }) =>
            feature.key === 'arc-mosaico-project' && feature.state === 'on',
        ),
        resolved: true,
      }
    })
    .catch(() => {
      // Drop the failed promise so a later mount can retry
      schoolScopedRequest = null
      return { enabled: false, resolved: false }
    })

  return schoolScopedRequest
}

/** Test-only: clears the shared school-scoped request between specs */
export const resetSchoolScopedArcBrandingCache = (): void => {
  schoolScopedRequest = null
}

export const readStoredArcBranding = (): boolean => {
  try {
    return window.localStorage.getItem(ARC_BRANDING_STORAGE_KEY) === 'true'
  } catch (err) {
    return false
  }
}

export const storeArcBranding = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(ARC_BRANDING_STORAGE_KEY, String(enabled))
  } catch (err) {
    console.error(err)
  }
}

/**
 * Arc branding (AR-797): starts from the cached flag value (no rebranding
 * flash for returning Arc users), then follows the school-scoped features
 * endpoint once it resolves — the per-school authority — falling back to the
 * global Flipper flag only when there is no school context (logged-out
 * pages). Also toggles `html.arc-branding` for the non-Mantine CSS rules in
 * themes/arc-branding.css.
 */
export const useArcBranding = (): boolean => {
  const { enabled: isArcFlagEnabled, loading, failed } = useSpecificFeature('arc-mosaico-project')
  const [isArcBrandingEnabled, setIsArcBrandingEnabled] = useState(readStoredArcBranding)
  // null while the school-scoped answer is still in flight
  const [schoolScoped, setSchoolScoped] = useState<ISchoolScopedResult | null>(null)

  useEffect(() => {
    let active = true
    fetchSchoolScopedArcBranding().then(result => {
      if (!active) return
      setSchoolScoped(result)
      if (result.resolved) {
        setIsArcBrandingEnabled(result.enabled)
        storeArcBranding(result.enabled)
      }
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    // Flipper fallback: only after the school-scoped source answered without
    // a verdict, and only with a real Flipper response — a fetch failure
    // never downgrades an Arc user to legacy (keeps the cached value)
    if (schoolScoped === null || schoolScoped.resolved) return
    if (loading || failed) return
    setIsArcBrandingEnabled(isArcFlagEnabled)
    storeArcBranding(isArcFlagEnabled)
  }, [schoolScoped, isArcFlagEnabled, loading, failed])

  useEffect(() => {
    if (!isArcBrandingEnabled) return
    document.documentElement.classList.add('arc-branding')
    // Cross-document view transitions: enables the browser-native cross-fade
    // when navigating between the SPA and the monolith (Chrome 126+, silently
    // ignored elsewhere). `@view-transition` is an at-rule and CANNOT be
    // scoped under html.arc-branding in themes/arc-branding.css, so it is
    // injected here instead — only while the arc branding is active — keeping
    // legacy users completely unaffected.
    const viewTransitionStyle = document.createElement('style')
    viewTransitionStyle.id = ARC_VIEW_TRANSITION_STYLE_ID
    viewTransitionStyle.textContent = '@view-transition { navigation: auto; }'
    document.head.appendChild(viewTransitionStyle)
    return () => {
      document.documentElement.classList.remove('arc-branding')
      viewTransitionStyle.remove()
    }
  }, [isArcBrandingEnabled])

  return isArcBrandingEnabled
}
